import { type ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styles from './toast.module.css'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

export interface ToastContextValue {
  toasts: Toast[]
  addToast: (type: ToastType, message: string, duration?: number) => void
  removeToast: (id: string) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
  maxToasts?: number
  defaultDuration?: number
}

export function ToastProvider({ children, maxToasts = 5, defaultDuration = 3000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const counterRef = useRef(0)

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback(
    (type: ToastType, message: string, duration = defaultDuration) => {
      counterRef.current += 1
      const id = `toast-${Date.now()}-${counterRef.current}`
      const toast: Toast = { id, type, message, duration }

      setToasts((prev) => {
        const updated = [...prev, toast]
        return updated.slice(-maxToasts)
      })
    },
    [defaultDuration, maxToasts]
  )

  const success = useCallback(
    (message: string, duration?: number) => addToast('success', message, duration),
    [addToast]
  )

  const error = useCallback(
    (message: string, duration?: number) => addToast('error', message, duration),
    [addToast]
  )

  const warning = useCallback(
    (message: string, duration?: number) => addToast('warning', message, duration),
    [addToast]
  )

  const info = useCallback(
    (message: string, duration?: number) => addToast('info', message, duration),
    [addToast]
  )

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onRemove: (id: string) => void
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div className={styles.container} role="region" aria-label="通知">
      {toasts.map((toast, index) => (
        <ToastItem key={toast.id} toast={toast} index={index} onRemove={onRemove} />
      ))}
    </div>
  )
}

interface ToastItemProps {
  toast: Toast
  index: number
  onRemove: (id: string) => void
}

function ToastItem({ toast, index, onRemove }: ToastItemProps) {
  const icons: Record<ToastType, ReactNode> = {
    success: (
      <svg viewBox="0 0 20 20" className={styles.icon}>
        <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6 10l3 3 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    error: (
      <svg viewBox="0 0 20 20" className={styles.icon}>
        <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 7l6 6M13 7l-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    warning: (
      <svg viewBox="0 0 20 20" className={styles.icon}>
        <path d="M10 2L2 18h16L10 2z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M10 8v4M10 14v1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    info: (
      <svg viewBox="0 0 20 20" className={styles.icon}>
        <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 9v5M10 6v1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  }

  useEffect(() => {
    if (toast.duration === 0) return
    const timer = setTimeout(() => {
      onRemove(toast.id)
    }, toast.duration || 3000)
    return () => clearTimeout(timer)
  }, [toast, onRemove])

  return (
    <div
      className={`${styles.toast} ${styles[toast.type]}`}
      role="alert"
      style={{ '--index': index } as React.CSSProperties}
    >
      <span className={styles.iconWrapper}>{icons[toast.type]}</span>
      <span className={styles.message}>{toast.message}</span>
      <button
        className={styles.closeButton}
        onClick={() => onRemove(toast.id)}
        aria-label="关闭通知"
        type="button"
      >
        <svg viewBox="0 0 16 16" className={styles.closeIcon}>
          <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}